import { Avatar, MenuList, MenuListOptions } from "@kolmeo/ui-components";
import { useTheme } from "@kolmeo/ui-core";
import { useCurrentUser, useMediaQuery } from "@src/hooks";
import { useTranslation } from "react-i18next";

import { featureList, useFeature, utilGetFeatureFlagFromListByTag } from "@src/lib/FeatureFlags";
import { useCustomNavigation } from "@src/navigation/hooks/useCustomNavigation";
import { useGlobalTenant } from "@src/navigation/hooks/useGlobalTenant";
import { ProfileType, TAC_URL } from "@src/types";
import { Dimensions, Linking } from "react-native";
import { useAuth } from "react-oidc-context";
import { b2cSettings } from "@src/lib/AzureAuth/AuthProvider/b2cSettings";

import { transformProfileTypeId } from "@src/utils";
import { useProfileTypeSwitcher } from "@src/hooks/useProfileTypeSwitcher/useProfileTypeSwitcher";
import { useTenantFeatureFlags } from "@src/screens/Tenant/hooks/useTenantFeatureFlags";
import { useMemo } from "react";

const DropdownMenu = () => {
  const { t } = useTranslation(["Common", "Tenant"]);

  const { currentUser } = useCurrentUser();
  const { tenancyId, hasMultipleTenancies } = useGlobalTenant();

  const { signoutRedirect } = useAuth();
  const { colors, palette } = useTheme();
  const { isBelowTablet } = useMediaQuery();
  const { navigateToScreen } = useCustomNavigation();
  const { data: featureFlags } = useTenantFeatureFlags(tenancyId);
  const showTenantsBankAccountDetailsOption = useMemo(
    () => utilGetFeatureFlagFromListByTag(featureFlags, featureList.tenantBankAccountDetails),
    [featureFlags]
  );

  const { userHasOwnerAndTenantProfiles, getAlternativeProfileTypeForUser, handleProfileSwitch } = useProfileTypeSwitcher();

  const menuOptions: MenuListOptions[] = [
    {
      readOnlyOption: true,

      title: `${currentUser?.emailAddress}`,
      textColor: palette.text.primary,
      headerTitle: transformProfileTypeId(currentUser?.profileTypeId),
      headerTitleBold: true,
      titleBold: false,
      secondaryTitleBold: false
    },
    ...(currentUser?.profileTypeId === ProfileType.tenant && showTenantsBankAccountDetailsOption?.enabled
      ? [
          {
            title: t("Tenant:PaymentMethods.PaymentSettingsHeader"),
            textColor: palette.text.primary,
            onPress: () => navigateToScreen("PaymentSettings", { screen: "Details", tenancyId }),
            dataUsageId: "global-payments-settings-click"
          }
        ]
      : []),
    ...(userHasOwnerAndTenantProfiles
      ? [
          {
            title: t("Common:Generic.SwitchProfile", { profileName: transformProfileTypeId(getAlternativeProfileTypeForUser) }),
            onPress: () => handleProfileSwitch(),
            titleBold: true,
            dataUsageId: "global-switch-profile-click"
          }
        ]
      : []),
    {
      title: t("Logout"),
      textColor: palette.text.primary,
      iconType: "signout",
      onPress: async () => await signoutRedirect({ post_logout_redirect_uri: `${b2cSettings.redirectUrl}logout` }),
      dataUsageId: "global-log-out-click-link"
    },
    {
      title: t("Common:Generic.TAC"),
      textColor: palette.text.primary,
      isLink: true,
      onPress: () => Linking.openURL(TAC_URL),
      dataUsageId: "global-terms-conditions-click"
    }
  ];
  return (
    <MenuList
      triggerComponent={
        <Avatar
          backgroundColor={colors.background}
          userType="Owner"
          name={currentUser ? { firstName: currentUser.firstName, lastName: currentUser.lastName } : undefined}
          data-usage-id="global-user-profile-click"
        />
      }
      options={menuOptions}
      optionsContainerStyle={{
        display: "flex",
        backgroundColor: colors.background,
        minWidth: isBelowTablet ? Dimensions.get("screen").width - 100 : Dimensions.get("screen").width / 5,
        maxWidth: isBelowTablet ? Dimensions.get("screen").width : Dimensions.get("screen").width / 5
      }}
    />
  );
};

export default DropdownMenu;
